import axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { optionallyAnonymizeRequest } from "@/anonymization/useAnonymize";
import { getAccessToken, getTokens, saveTokens, clearTokens } from "@/auth";

/**
 * Base API URL without trailing slash
 */
export const API_URL = import.meta.env.VITE_API_URL.replace(/\/$/, '');

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use((request) => {
  let authToken = getAccessToken();
  if (authToken) {
    request.headers["Authorization"] = `Bearer ${authToken}`;
  }

  optionallyAnonymizeRequest(request);

  return request;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.request.responseURL.includes("api/token/refresh/")) {
      const tokens = getTokens();
      if (tokens) {
        clearTokens();
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

export const refreshToken = async () => {
  const tokens = getTokens();
  if (!tokens) return;

  try {
    const res = await apiClient.post("token/refresh/", {
      refresh: tokens.refresh,
    });
    const data = await res.data;
    saveTokens({ ...tokens, access: data.access });
    return data;
  } catch (err) {
    if (getTokens()) {
      clearTokens();
      // TODO: proper navigation?
      window.location.reload();
    }
  }
};

// Function that will be called to refresh authorization
const refreshAuthLogic = (failedRequest: any) =>
  refreshToken().then((tokenRefreshResponse) => {
    failedRequest.response.config.headers["Authorization"] =
      "Bearer " + tokenRefreshResponse.access;
    return Promise.resolve();
  });

// Instantiate the interceptor
// @ts-ignore
createAuthRefreshInterceptor(apiClient, refreshAuthLogic, {
  shouldRefresh: (error: any) =>
    error?.response?.data?.code === "token_not_valid",
});
